import React from 'react';

/**
 * A single pill badge. `variant="warning"` combines the base `.compact-pill`
 * with `.warning-pill`.
 */
export const CompactPill = ({ variant, children }) => (
  <span className={`compact-pill${variant === 'warning' ? ' warning-pill' : ''}`}>{children}</span>
);

/**
 * Renders the pills row (`.compact-meta`) from an array of
 * { label, variant } descriptors, or accepts arbitrary children.
 */
export const CompactMeta = ({ pills, children }) => (
  <div className="compact-meta">
    {pills
      ? pills.map((pill, index) => (
          <CompactPill key={pill.key ?? `${pill.label}-${index}`} variant={pill.variant}>
            {pill.label}
          </CompactPill>
        ))
      : children}
  </div>
);

/**
 * The workhorse of the scaffold: a compact table row with inline actions that
 * can optionally expand into a detail row. Emits the generic,
 * column-configurable `.crud-row-content` grid.
 *
 * Use it two ways:
 *   - Flat records (lookup tables): pass `actions` and omit `detail`. The row
 *     shows its fields with inline action buttons and does NOT expand — no
 *     drill-in makes sense when a record has no children.
 *   - Records with deeper data (schools, events): pass `actions` AND `detail`.
 *     Actions stay inline (act without expanding), and clicking the row toggles
 *     the detail panel for the full breakdown.
 *
 * The row is a flex layout: `main` grows, meta/schedule/flags take their
 * natural width, and the actions/chevron tail is pinned to the right edge.
 *
 * Props:
 *   colSpan       td colSpan (default 1, matching the single-column table)
 *   main          { title, subtitle? }  -> .compact-main
 *   pills?        array of { label, variant? } -> .compact-meta
 *   schedule?     node -> .compact-schedule cell
 *   flags?        node -> .compact-flags cell
 *   extraCells?   arbitrary extra cells
 *   actions?      node rendered inline on the row (clicks don't toggle expand)
 *   detail?       expanded-row content; presence enables expand/collapse
 *   expandable?   force expandable on/off (default: truthy `detail`)
 *   isOpen, onToggle   expand state (required when expandable)
 *   expandHint    show the chevron affordance (default true)
 */
const ExpandableRow = ({
  colSpan = 1,
  main,
  pills,
  schedule,
  flags,
  extraCells,
  actions,
  detail,
  expandable,
  isOpen,
  onToggle,
  expandHint = true,
}) => {
  const canExpand = expandable != null ? expandable : Boolean(detail);

  const stopClick = (event) => event.stopPropagation();

  return (
    <>
      <tr
        className={`compact-row${canExpand ? '' : ' is-static'}`}
        onClick={canExpand ? onToggle : undefined}
        style={{ cursor: canExpand ? 'pointer' : 'default' }}
      >
        <td colSpan={colSpan}>
          <div className="crud-row-content">
            <div className="compact-main">
              {main?.title != null ? <div className="compact-title">{main.title}</div> : null}
              {main?.subtitle != null ? (
                <div className="compact-subtitle">{main.subtitle}</div>
              ) : null}
            </div>

            {pills && pills.length ? <CompactMeta pills={pills} /> : null}
            {schedule ? <div className="compact-schedule">{schedule}</div> : null}
            {flags ? <div className="compact-flags">{flags}</div> : null}
            {extraCells}

            <div className="crud-row-tail">
              {actions ? (
                <div className="crud-row-actions" onClick={stopClick}>
                  {actions}
                </div>
              ) : null}
              {canExpand && expandHint ? (
                <span className="text-muted small">{isOpen ? '▼' : '▶'}</span>
              ) : null}
            </div>
          </div>
        </td>
      </tr>

      {canExpand && isOpen && detail ? (
        <tr>
          <td colSpan={colSpan}>{detail}</td>
        </tr>
      ) : null}
    </>
  );
};

export default ExpandableRow;
